import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import generateAdminTransferTokenMutation from 'shared/graphql/user/mutations/generate-admin-transfer-token.gql';
import PublicEnv from 'shared/utils/public-env';
import { GqlGenerateAdminTransferTokenMutation, GqlGenerateAdminTransferTokenMutationVariables } from 'types/graphql';

import { DutchieMark } from 'src/assets/dutchie-mark';
import { EditorAssemblyLoader } from 'src/components/cms-editor-frame/editor-assembly-loader';
import {
  consumeArmedCollapse,
  NAV_COLLAPSE_MS,
  NAV_COLLAPSED_WIDTH,
  NAV_EXPAND_MS,
  NAV_EXPANDED_WIDTH,
  NAV_LOGO_FADE_IN_MS,
  NAV_RENDERED_FALLBACK_MS,
  NAV_RENDERED_SETTLE_MS,
  prefersReducedMotion,
} from 'src/components/sidebar/nav-chrome-transition';
import { useStores } from 'src/hooks/use-stores';

/**
 * Below 638px Puck drops its left plugin-bar rail entirely (`--puck-pluginbar-width: 68px` is
 * scoped to `@media (min-width: 638px)` in `@puckeditor/core@0.23.0`'s `dist/index.css`) -- don't
 * animate a rail toward/from something that doesn't exist on the other side of the handoff.
 */
const PUCK_RAIL_MIN_VIEWPORT_PX = 638;

/**
 * Admin content area's background (matches `content-container.jsx`'s `OuterContainer`). Used for
 * the exit overlay's body so the handoff doesn't pop through the editor's `#f5f5f5` loading tone,
 * and exported for the editor route's own page background to avoid a bare-white first paint.
 */
export const ADMIN_CONTENT_BACKGROUND = '#f4f7fa';

/**
 * `transitionend` can be dropped (hidden element, backgrounded tab, interrupted transition),
 * which would strand the user on a frozen overlay -- so `onNavigate` is always raced against this
 * fixed fallback, with a little slack on top of the expand duration.
 */
const EXIT_FALLBACK_SLACK_MS = 100;

/**
 * Puck's `LogoCorner` green fill border colour, measured via pixel analysis of a live screenshot
 * (`@puckeditor/core@0.23.0`, 1440x900, dpr 1). A Puck upgrade could shift this.
 */
const RAIL_BORDER_COLOR = 'rgb(225,230,235)';

/**
 * Ghost logo geometry, measured against the same screenshot: a 36x36 box flex-centred in the
 * 67x67 top-left corner (RAIL_GREEN_WIDTH square). Using flex-centring rather than hand-placed
 * offsets lands it exactly on Puck's real logo so nothing shifts once this overlay is removed.
 */
const RAIL_GREEN_WIDTH = 67;
const LOGO_MARK_SIZE = 36;
const LOGO_COLOR = '#f1e8d6';

/**
 * Deep link / refresh only: no shrink to key the ghost logo's fade off, so it mounts after this
 * short fixed beat instead -- long enough to avoid a jarring pop-in, short enough not to read as
 * a stall.
 */
const NAV_LOGO_DEEP_LINK_DELAY_MS = 50;

/**
 * Whether Puck's own rail exists in the current viewport. Treats missing `matchMedia`
 * (test/SSR) as "no rail" rather than guessing.
 */
function hasPuckRail(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  return window.matchMedia(`(min-width: ${PUCK_RAIL_MIN_VIEWPORT_PX}px)`).matches;
}

/**
 * VAPE serves each dev instance's apps at sibling ingress hostnames differing only by a leading
 * label -- admin at `admin--<key>.<domain>`, consumer at `consumer--<key>.<domain>` (see
 * `bolt.yaml`'s `VAPE=1` block). Keep these prefixes in sync with it.
 */
const VAPE_ADMIN_HOST_PREFIX = 'admin--';
const VAPE_CONSUMER_HOST_PREFIX = 'consumer--';

/**
 * Resolves the origin the CMS editor iframe loads from.
 *
 * Normally `PublicEnv.consumerUrl`. In a frontend-only VAPE instance, though, the admin app
 * proxies its public config from the shared dev API, so `consumerUrl` points at the deployed
 * dev consumer instead of this instance -- the editor would show deployed code, not local
 * changes. When served from a VAPE `admin--` host, derive the sibling `consumer--` host instead,
 * swapping only the leading label and keeping the rest of the host byte-for-byte. That keeps the
 * result on the same parent domain as the trusted admin origin -- important since `EDITOR_ORIGIN`
 * is the `postMessage` target for a short-lived admin transfer token, so the swap can never
 * redirect it off-domain. Production admin hosts never start with `admin--`, so this is inert
 * outside VAPE.
 */
export function resolveEditorOrigin(
  location: Pick<Location, 'host' | 'protocol'> | undefined = typeof window === 'undefined'
    ? undefined
    : window.location
): string {
  const fallback = new URL(PublicEnv.consumerUrl || 'https://dutchie.com').origin;
  if (!location) return fallback;
  const { host, protocol } = location;
  // Require a non-empty suffix -- a bare `admin--` host has no sibling to derive.
  const suffix = host.startsWith(VAPE_ADMIN_HOST_PREFIX) ? host.slice(VAPE_ADMIN_HOST_PREFIX.length) : '';
  if (suffix) return `${protocol}//${VAPE_CONSUMER_HOST_PREFIX}${suffix}`;
  return fallback;
}

const EDITOR_ORIGIN = resolveEditorOrigin();

/** The default CMS model opened when a caller doesn't specify one. */
const DEFAULT_MODEL_ID = 'standalone-page';
/** The default content variant opened when a caller doesn't specify one. */
const DEFAULT_VARIANT_KEY = 'draft';

/** Lifecycle messages the embedded editor posts back to this frame. */
type CmsMessage = {
  type: 'cms:error' | 'cms:navigate' | 'cms:published' | 'cms:ready' | 'cms:rendered';
  payload?: Record<string, unknown>;
};

type CmsEditorFrameProps = {
  /** Enterprise whose content is being edited. Used to build the editor URL. */
  enterpriseId: string;
  /**
   * Store cName the editor previews against. The editor resolves a dispensary from
   * this value, so without it the live preview has no store. Appended to the built
   * URL as `&cName=<cName>`; ignored when `editorPath` is supplied.
   */
  cName?: string;
  /**
   * True while the caller is still resolving `cName` and hasn't decided its final value
   * (distinct from simply omitting it, which loads immediately). While pending, the chrome
   * overlay shows but the `<iframe>` doesn't mount -- avoids loading with a missing `cName`
   * and then reloading once the real value arrives.
   */
  cNamePending?: boolean;
  /**
   * CMS model to edit. Maps directly to the editor route segment
   * (`/cms/<enterpriseId>/<modelId>`). Defaults to `standalone-page`.
   */
  modelId?: string;
  /** Content variant to open (e.g. `draft`). Defaults to `draft`. */
  variantKey?: string;
  /**
   * Escape hatch: a fully-formed editor path (`/cms/...?...`) that overrides the
   * URL built from `enterpriseId`/`modelId`/`variantKey`. Prefer the semantic
   * props; reach for this only for routes this component can't yet express.
   */
  editorPath?: string;
  /** Fired once the editor has loaded and been authenticated. */
  onReady?: () => void;
  /** Fired when the user publishes; payload carries the published variant details. */
  onPublished?: (payload?: Record<string, unknown>) => void;
  /** Fired on an editor-side error; payload carries the error details. */
  onError?: (payload?: Record<string, unknown>) => void;
  /**
   * Fired when the editor requests host navigation (e.g. back to a model list). Contract:
   * this must actually route away from this component -- the exit overlay is intentionally
   * not self-dismissing, so a no-op handler strands the user on a frozen full-screen overlay.
   */
  onNavigate?: (payload?: Record<string, unknown>) => void;
};

/**
 * Drop-in iframe host for the Dutchie CMS drag-and-drop editors.
 *
 * Hand it an `enterpriseId` (plus optional `modelId`/`variantKey`) and it takes
 * care of everything needed to display a working editor: it builds the editor
 * URL, loads it in a fixed-origin iframe, generates a short-lived admin transfer
 * token, and hands that token to the editor over `postMessage` once the editor
 * signals `cms:ready` — so no auth token ever appears in a URL. Editor lifecycle
 * events surface through the `on*` callbacks.
 *
 * The component owns none of its own chrome or routing, so any admin surface can
 * mount it directly to prototype a fuller CMS studio around it:
 *
 * ```tsx
 * <CmsEditorFrame
 *   enterpriseId={enterpriseId}
 *   variantKey='draft'
 *   onPublished={() => showToast('Published!')}
 * />
 * ```
 */
export function CmsEditorFrame({
  enterpriseId,
  cName,
  cNamePending = false,
  modelId = DEFAULT_MODEL_ID,
  variantKey = DEFAULT_VARIANT_KEY,
  editorPath,
  onReady,
  onPublished,
  onError,
  onNavigate,
}: CmsEditorFrameProps): JSX.Element {
  const { apolloClient } = useStores();
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Read once at mount and frozen -- re-evaluating any of these mid-animation could switch
  // strategy partway through a transition already in flight.
  const animateEnterRef = useRef(consumeArmedCollapse());
  const hasRailRef = useRef(hasPuckRail());
  const reducedMotionRef = useRef(prefersReducedMotion());

  // Only slide when there's a real click-driven arrival, a rail to slide, and motion isn't reduced.
  const shouldAnimateEnterRef = useRef(hasRailRef.current && animateEnterRef.current && !reducedMotionRef.current);

  // Ghost logo is eligible whenever the rail itself is shown; cases differ only in when it
  // mounts and whether the mount fades in (see ghostLogoMounted below).
  const ghostLogoEligibleRef = useRef(hasRailRef.current);

  // Rail width for the entering overlay. Starts wide only when we'll animate the collapse;
  // otherwise starts at resting value so there's nothing to snap from.
  const [enterRailWidth, setEnterRailWidth] = useState(() =>
    shouldAnimateEnterRef.current ? NAV_EXPANDED_WIDTH : NAV_COLLAPSED_WIDTH
  );
  // True once the collapse slide has run its course. The overlay only dismisses once
  // `ready && slideDone` -- a warm-cached iframe firing cms:ready instantly must not cut the
  // slide short.
  const [slideDone, setSlideDone] = useState(() => !shouldAnimateEnterRef.current);

  // Two-tick mount: mount at opacity 0, flip to visible next frame -- a same-commit opacity
  // change won't transition. Reduced motion mounts visible immediately (nothing to key a fade
  // off). Otherwise mounts on shrink-complete (armed) or after a short fixed beat (deep link).
  const [ghostLogoMounted, setGhostLogoMounted] = useState(
    () => ghostLogoEligibleRef.current && reducedMotionRef.current
  );
  const [ghostLogoVisible, setGhostLogoVisible] = useState(
    () => ghostLogoEligibleRef.current && reducedMotionRef.current
  );

  // Whether the embedded editor confirmed it actually painted (`cms:rendered`). Stronger than
  // `ready` (auth handshake only) -- dismissing on `ready` alone reintroduces the "blank editor"
  // flash this overlay prevents.
  const [rendered, setRendered] = useState(false);
  // True once the enter overlay is allowed to fade away. See the effect below for the gate.
  const [overlayCleared, setOverlayCleared] = useState(false);

  // Exit state: set once cms:navigate arrives. The overlay mounts over the ready iframe with
  // its rail sitting exactly over Puck's own rail, then expands back out before navigating.
  const [exiting, setExiting] = useState(false);
  const [exitRailWidth, setExitRailWidth] = useState(NAV_COLLAPSED_WIDTH);
  const exitPayloadRef = useRef<Record<string, unknown> | undefined>(undefined);
  // Consume-once guard: transitionend and the fallback timeout both call finishExit; only
  // one may actually invoke onNavigate.
  const exitFiredRef = useRef(false);

  const finishExit = useCallback(() => {
    if (exitFiredRef.current) return;
    exitFiredRef.current = true;
    // `exiting` never resets: onNavigate must route away from this component.
    onNavigate?.(exitPayloadRef.current);
  }, [onNavigate]);

  // Kicks off the collapse: rAF ensures the starting width paints before the change to the
  // end value, or the browser sees no transition, just an instant jump.
  useEffect(() => {
    if (!shouldAnimateEnterRef.current) return undefined;
    const rafId = requestAnimationFrame(() => setEnterRailWidth(NAV_COLLAPSED_WIDTH));
    const timeoutId = setTimeout(() => {
      setSlideDone(true);
      setGhostLogoMounted(true);
    }, NAV_COLLAPSE_MS);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, []);

  // Deep link / refresh only: no shrink to key the mount off, so mount after a short fixed
  // beat instead of leaving the rail bare for the whole load.
  useEffect(() => {
    if (!ghostLogoEligibleRef.current || shouldAnimateEnterRef.current || reducedMotionRef.current) {
      return undefined;
    }
    const timeoutId = setTimeout(() => setGhostLogoMounted(true), NAV_LOGO_DEEP_LINK_DELAY_MS);
    return () => clearTimeout(timeoutId);
  }, []);

  // Flips opacity a frame after mount, giving the transition a "from" state to animate.
  useEffect(() => {
    if (!ghostLogoMounted || ghostLogoVisible) return undefined;
    const rafId = requestAnimationFrame(() => setGhostLogoVisible(true));
    return () => cancelAnimationFrame(rafId);
  }, [ghostLogoMounted, ghostLogoVisible]);

  // Gates the enter overlay's dismissal. `ready && slideDone` is a floor that must always be
  // met first. Past that: a short settle beat if the editor confirmed it painted (`rendered`),
  // otherwise a generous fallback timeout so a build that never sends cms:rendered can't strand
  // the user forever.
  useEffect(() => {
    if (!ready || !slideDone || overlayCleared) return undefined;
    const delayMs = rendered ? NAV_RENDERED_SETTLE_MS : NAV_RENDERED_FALLBACK_MS;
    const timeoutId = setTimeout(() => setOverlayCleared(true), delayMs);
    return () => clearTimeout(timeoutId);
  }, [ready, slideDone, rendered, overlayCleared]);

  useEffect(() => {
    if (!exiting) return undefined;
    const rafId = requestAnimationFrame(() => setExitRailWidth(NAV_EXPANDED_WIDTH));
    const timeoutId = setTimeout(finishExit, NAV_EXPAND_MS + EXIT_FALLBACK_SLACK_MS);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(timeoutId);
    };
  }, [exiting, finishExit]);

  // Build the editor URL from the semantic props unless an explicit override is supplied, then
  // ensure embed mode so the editor hides its standalone chrome. `null` while `cNamePending`
  // means the URL can't be computed yet -- must not mount an iframe with a partial URL.
  const iframeSrc = useMemo(() => {
    if (cNamePending) return null;
    const cNameParam = cName ? `&cName=${encodeURIComponent(cName)}` : '';
    const path =
      editorPath ??
      `/cms/${encodeURIComponent(enterpriseId)}/${encodeURIComponent(modelId)}?variantKey=${encodeURIComponent(
        variantKey
      )}${cNameParam}`;
    if (path.includes('embed=')) return `${EDITOR_ORIGIN}${path}`;
    return `${EDITOR_ORIGIN}${path}${path.includes('?') ? '&' : '?'}embed=true`;
  }, [cNamePending, editorPath, enterpriseId, cName, modelId, variantKey]);

  // Tracks the last iframeSrc this reset effect acted on, to tell "URL just became computable"
  // apart from "URL changed for an already-loaded iframe" (see branch below).
  const lastResolvedSrcRef = useRef<string | null>(null);

  useEffect(() => {
    // Still cNamePending (or no editorPath/cName ahead of first render) -- no session to reset.
    if (iframeSrc === null) return;
    const isFirstResolution = lastResolvedSrcRef.current === null;
    lastResolvedSrcRef.current = iframeSrc;
    // First resolution is a late `cName` arriving, not a new iframe replacing an old one --
    // nothing was ever mounted, so there's nothing to invalidate. Doesn't touch enter-animation
    // state (enterRailWidth/slideDone/ghost logo), which is driven separately by the mount-only
    // collapse effect.
    if (isFirstResolution) return;
    setReady(false);
    setError(null);
    // A fast re-navigate supersedes any exit in flight for the previous iframe -- otherwise a
    // stale exitFiredRef could swallow the new iframe's own cms:navigate, or vice versa.
    setExiting(false);
    exitFiredRef.current = false;
    // New iframe, new session: previous cms:rendered says nothing about this one.
    setRendered(false);
    setOverlayCleared(false);
  }, [iframeSrc]);

  /** Generates a transfer token and sends it to the iframe via postMessage. */
  const sendAuthToIframe = useCallback(async () => {
    try {
      const { data } = await apolloClient.mutate<
        GqlGenerateAdminTransferTokenMutation,
        GqlGenerateAdminTransferTokenMutationVariables
      >({
        mutation: generateAdminTransferTokenMutation,
      });

      const transferToken = data?.generateAdminTransferToken?.transferToken;
      if (!transferToken) {
        setError('Failed to generate auth token');
        return;
      }

      iframeRef.current?.contentWindow?.postMessage(
        { type: 'cms:auth', payload: { token: transferToken } },
        EDITOR_ORIGIN
      );
      setReady(true);
      onReady?.();
    } catch (e) {
      setError('Failed to authenticate editor');
      console.error(e);
    }
  }, [apolloClient, onReady]);

  // On cms:navigate, either hand off to onNavigate right away (no rail to animate) or arm the
  // exit overlay -- rail starts collapsed over Puck's own rail (invisible handoff) and expands
  // before finishExit calls onNavigate.
  const handleCmsNavigate = useCallback(
    (payload?: Record<string, unknown>) => {
      if (!hasRailRef.current || reducedMotionRef.current) {
        onNavigate?.(payload);
        return;
      }
      exitPayloadRef.current = payload;
      setExitRailWidth(NAV_COLLAPSED_WIDTH);
      setExiting(true);
    },
    [onNavigate]
  );

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== EDITOR_ORIGIN) return;
      // While `cNamePending` no `<iframe>` is mounted, so there's no legitimate source for this
      // message -- without this guard a same-origin message could drive sendAuthToIframe before
      // any iframe exists to authenticate.
      if (!iframeRef.current) return;
      if (event.source !== iframeRef.current.contentWindow) return;

      const msg = event.data as CmsMessage | null;
      if (typeof msg?.type !== 'string' || !msg.type.startsWith('cms:')) return;

      switch (msg.type) {
        case 'cms:ready':
          void sendAuthToIframe();
          break;
        case 'cms:rendered':
          setRendered(true);
          break;
        case 'cms:published':
          onPublished?.(msg.payload);
          break;
        case 'cms:error':
          onError?.(msg.payload);
          break;
        case 'cms:navigate':
          handleCmsNavigate(msg.payload);
          break;
      }
    },
    [sendAuthToIframe, onPublished, onError, handleCmsNavigate]
  );

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Guard on both the property (not an incidental `background` transition) and the target
  // (not a bubbled event from some descendant).
  const handleRailTransitionEnd = useCallback(
    (event: React.TransitionEvent<HTMLDivElement>) => {
      if (event.propertyName !== 'width' || event.target !== railRef.current) return;
      if (exiting) finishExit();
    },
    [exiting, finishExit]
  );

  if (error) {
    return <ErrorState>{error}</ErrorState>;
  }

  // Overlay is up while entering (until overlayCleared) or exiting (until finishExit).
  const showOverlay = exiting || !overlayCleared;
  let railDurationMs: number | null = null;
  if (exiting) {
    railDurationMs = NAV_EXPAND_MS;
  } else if (shouldAnimateEnterRef.current) {
    railDurationMs = NAV_COLLAPSE_MS;
  }
  const currentRailWidth = exiting ? exitRailWidth : enterRailWidth;
  // Border only belongs at the Puck-handoff width; transparent at NAV_EXPANDED_WIDTH so
  // nothing's left against the (borderless) admin sidebar once exit expand finishes.
  const railBorderColor = currentRailWidth === NAV_COLLAPSED_WIDTH ? RAIL_BORDER_COLOR : 'transparent';
  // Ghost logo only renders on the entering side, once mounted -- must be genuinely absent
  // from the DOM before the shrink completes, not just invisible.
  const showGhostLogo = ghostLogoEligibleRef.current && !exiting && ghostLogoMounted;

  return (
    <FrameContainer>
      {showOverlay &&
        (hasRailRef.current ? (
          <ChromeOverlay data-testid='cms-loading-overlay'>
            <ChromeRail
              ref={railRef}
              data-testid='chrome-rail'
              style={{ width: `${currentRailWidth}px`, borderRightColor: railBorderColor }}
              $durationMs={railDurationMs}
              onTransitionEnd={handleRailTransitionEnd}
            />
            {showGhostLogo && (
              <GhostLogo data-testid='cms-ghost-logo' $visible={ghostLogoVisible}>
                <DutchieMark width={LOGO_MARK_SIZE} height={LOGO_MARK_SIZE} color={LOGO_COLOR} />
              </GhostLogo>
            )}
            <ChromeBody $background={exiting ? ADMIN_CONTENT_BACKGROUND : '#f5f5f5'}>
              {!exiting && <EditorAssemblyLoader />}
            </ChromeBody>
          </ChromeOverlay>
        ) : (
          // Not gated on `!exiting` like the ChromeBody branch above: `exiting` can only ever
          // become true via handleCmsNavigate's rail-expand path, which itself only runs when
          // `hasRailRef.current` is true (see the early `if (!hasRailRef.current || ...)
          // { onNavigate?.(...); return; }` guard). This branch renders precisely when
          // `hasRailRef.current` is false, so `exiting` is unreachable here -- there is no exit
          // overlay to suppress on the no-rail path, it hands off to onNavigate immediately.
          <LoadingOverlay>
            <EditorAssemblyLoader />
          </LoadingOverlay>
        ))}
      {/* No iframe while cNamePending -- avoids loading with a missing cName then reloading. */}
      {iframeSrc !== null && (
        <StyledIframe ref={iframeRef} src={iframeSrc} title='CMS Editor' allow='clipboard-write' />
      )}
    </FrameContainer>
  );
}

const FrameContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 64px);
`;

const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  font-size: 14px;
  color: #646d72;
  z-index: 1;
`;

// Full-height layer: a `#003d29` rail (matching the admin sidebar and Puck's plugin-bar) plus
// a body area for the loading copy / admin-background handoff. `z-index: 1` sits above
// StyledIframe (auto) regardless of DOM order.
const ChromeOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  z-index: 1;
`;

// border-box + 1px border-right means the green content box is naturally
// NAV_COLLAPSED_WIDTH - 1 = RAIL_GREEN_WIDTH (67px) at the handoff width, matching Puck's own
// fill + border construction with no separate width math.
const ChromeRail = styled.div<{ $durationMs: number | null }>`
  flex: 0 0 auto;
  height: 100%;
  box-sizing: border-box;
  background: #003d29;
  border-right: 1px solid transparent;
  transition: ${({ $durationMs }) =>
    $durationMs === null ? 'none' : `width ${$durationMs}ms ease, border-right-color ${$durationMs}ms ease`};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

// Absolutely positioned over ChromeRail so its position never shifts as the rail animates, and
// sits at the same coordinates as the real Puck logo underneath -- removing the overlay reads
// as "nothing moved".
const GhostLogo = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: ${RAIL_GREEN_WIDTH}px;
  height: ${RAIL_GREEN_WIDTH}px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transition: opacity ${NAV_LOGO_FADE_IN_MS}ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const ChromeBody = styled.div<{ $background: string }>`
  flex: 1 1 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $background }) => $background};
  font-size: 14px;
  color: #646d72;
`;

const StyledIframe = styled.iframe`
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 64px);
  border: none;
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: calc(100vh - 64px);
  font-size: 14px;
  color: #d32f2f;
`;
